import React, { type ComponentPropsWithoutRef, type FC, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PaymentLimitHelpText } from '@mobi/account/Areas/DepositLimit/Utils/PaymentLimitHelpText'
import { InitialData, PaymentMethod } from '@mobi/api-types'
import { PaymentAmount } from '@mobi/component-library/Areas/Account/Payment'
import type { PaymentMethods } from '@mobi/component-library/Areas/Deposit/types'
import { useToggleControl } from '@mobi/utils'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { DepositError, InvalidCardAlert, NativeUpgradeRequiredAlert } from '.'
import { useHostContext } from '../../../Common/HostContext'
import { amountInputValidationRegex } from '../../../Utils'
import { track } from '../../../Utils/analytics'
import {
  cancel,
  deposit,
  depositFailed,
  depositSucceeded,
  finished,
  selectDepositFlow,
  selectDepositState,
  setDepositInput,
  verify,
} from '../Store'
import { incrementSuccessCount } from '../Utils'
import { isNativeBraintreeAvailable } from '../Utils/isNativeBraintreeAvailable'
import { PanelStyled } from './DepositBody.styles'
import { DepositProgress } from './DepositProgress'
import { PaymentButton } from './Payments'
import type { OnFailureParams } from './types'

type Props = {
  accountNumber: number
  initialData: InitialData
  paymentMethod: PaymentMethods
  remainingDepositAmount: number
  remainingDepositTransactions: number
}

export const DepositPanel: FC<Props> = ({
  accountNumber,
  initialData,
  paymentMethod,
  remainingDepositAmount,
  remainingDepositTransactions,
}) => {
  const dispatch = useDispatch()
  const flow = useSelector(selectDepositFlow)
  const { events } = useHostContext()
  const { depositStatus, depositInput, depositFailure } = useSelector(selectDepositState)
  const { isOpen, open: openTray, close: closeTray } = useToggleControl()

  const inputValue = +depositInput.inputValue

  const onStart = useCallback(() => {
    dispatch(verify())
    openTray()

    track('deposit-started', {
      accountNumber,
      paymentMethod,
      depositVariant: flow,
    })
  }, [dispatch, openTray, accountNumber, paymentMethod, flow])

  const onCancel = useCallback(() => {
    dispatch(cancel())
    closeTray()

    track('deposit_cancel', {
      accountNumber,
      paymentMethod,
      cancelStep: depositStatus,
      depositVariant: flow,
    })
  }, [dispatch, closeTray, accountNumber, paymentMethod, depositStatus, flow])

  const onDepositing = useCallback(() => {
    if (paymentMethod !== 'CreditCard') {
      openTray()
    }
    dispatch(deposit())
  }, [dispatch, openTray, paymentMethod])

  const onSuccess = useCallback(
    (depositAmount: number) => {
      const successfulDepositCount = incrementSuccessCount(accountNumber)

      track('deposit-success', {
        accountNumber,
        paymentMethod,
        depositVariant: flow,
        deposit: {
          amount: depositAmount.toString(),
          isFirstDeposit: successfulDepositCount === 1,
          isSecondDeposit: successfulDepositCount === 2,
        },
      })

      return new Promise<void>(() => {
        events?.onPaymentSuccess(flow, depositAmount)
        dispatch(depositSucceeded({ accountNumber }))
      })
    },
    [accountNumber, dispatch, flow, paymentMethod, events]
  )

  const onFailure = useCallback(
    (details: DepositError | OnFailureParams) => {
      track('deposit-error', {
        accountNumber,
        paymentMethod,
        depositVariant: flow,
        errorTrigger: 'Deposit',
        message: details.message,
      })

      dispatch(
        depositFailed({
          ...details,
          displayMessage: details.displayMessage,
        })
      )
    },
    [accountNumber, paymentMethod, flow, dispatch]
  )

  const onDepositInputChanged = useCallback(
    (value: string) => {
      if (amountInputValidationRegex.test(value)) {
        dispatch(setDepositInput({ inputValue: value, isValid: true, amount: +value }))
      } else {
        dispatch(setDepositInput({ inputValue: value, isValid: false }))
      }
    },
    [dispatch]
  )

  const { depositLimits } = initialData

  const isDepositLimitReached =
    depositLimits.maximum < depositLimits.minimum ||
    remainingDepositAmount <= 0 ||
    remainingDepositTransactions <= 0

  const isDepositingAllowed =
    depositStatus === 'idle' &&
    depositInput.isValid &&
    depositInput.amount >= depositLimits.minimum &&
    depositInput.amount <= remainingDepositAmount &&
    !isDepositLimitReached

  const primaryCard = initialData.paymentMethods.find(card => card.isDefault)

  return (
    <PanelStyled>
      {paymentMethod === 'CreditCard' &&
        primaryCard !== undefined &&
        isCardInvalid(primaryCard) && <InvalidCardAlert card={primaryCard} />}
      {isReactNativeApp &&
        ['ApplePay', 'PayPal'].includes(paymentMethod) &&
        !isNativeBraintreeAvailable() && <NativeUpgradeRequiredAlert />}
      <PaymentAmount
        helpText={
          <PaymentLimitHelpText
            paymentType='deposit'
            inputValue={inputValue}
            paymentLimits={depositLimits}
            remainingPaymentAmount={remainingDepositAmount}
            remainingPaymentTransactions={remainingDepositTransactions}
            isDisabled={isDepositLimitReached}
            showDepositLimitLink={flow !== 'quick-deposit'}
          />
        }
        inputValue={depositInput.inputValue}
        onAmountChange={onDepositInputChanged}
        isDisabled={isDepositLimitReached}
        maxAmount={remainingDepositAmount}
      />
      <PaymentButton
        isNative={isReactNativeApp}
        paymentMethod={paymentMethod}
        initialData={initialData}
        isDepositAllowed={isDepositingAllowed}
        depositAmount={depositInput.isValid ? depositInput.amount : 0}
        accountNumber={accountNumber}
        onStart={onStart}
        onCancel={onCancel}
        onDepositing={onDepositing}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />

      <DepositProgress
        isOpen={isOpen}
        depositStatus={depositStatus}
        failureMessage={depositFailure?.displayMessage ?? 'An unexpected error has occurred'}
        depositAmount={depositInput.isValid ? depositInput.amount : 0}
        onClose={() => {
          dispatch(finished())
          closeTray()
        }}
      />
    </PanelStyled>
  )
}

function isCardInvalid(
  card: PaymentMethod
): card is ComponentPropsWithoutRef<typeof InvalidCardAlert>['card'] {
  return card.isExpired || !card.isDebitCard
}
